import { baseLayout } from "@/app/layouts/baseLayout";
import React, { Suspense } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import { PAGE_DATA } from "./constants/pages";
import instance from '@/shared/api/axios';
import { workspaceLoader } from "@/app/datapreload/workspaceLoader";
/**
 * 라우팅 설정 및 페이지 로딩을 담당하는 모듈
 * 
 * @module appRouter
 */

/**
 * 페이지 로딩 중 표시될 컴포넌트
 * 사용자에게 로딩 상태를 시각적으로 알립니다.
 */

/**
 * 인증 상태 체크 함수
 * 
 * @returns 인증 상태 여부
 */
const checkAuthStatus = async () => {
  // 🔥 public 경로는 인증 체크 스킵
  const publicPaths = ['/privacy-policy', '/privacy-policy.html', '/account-deletion', '/account-deletion.html'];
  if (publicPaths.some(path => window.location.pathname.includes(path))) {
    return true;
  }

  const hasAuthToken = document.cookie.includes('AUTH_TOKEN');
  if (!hasAuthToken) {
    return false;
  }

  try {
    const response = await instance.get('/v1/auth/me');
    return response.data.success === true;
  } catch (error) {
    return false;
  }
};

/**
 * 컴포넌트를 Suspense로 래핑하여 지연 로딩을 구현합니다.
 * 
 * @param Component - 지연 로딩할 React 컴포넌트
 * @returns Suspense로 래핑된 컴포넌트
 */
const withSuspense = (Component: React.ComponentType) => {
  return (
    <Suspense>
      <Component />
    </Suspense>
  );
};

/**
 * PAGE_DATA를 기반으로 라우트 설정을 생성합니다.
 * 각 페이지는 지연 로딩되며, 로딩 중에는 LoadingFallback이 표시됩니다.
 * 
 * @returns 라우트 설정 객체 배열
 */
const generateRoutes = () => {
  const routes: any[] = [];

  Object.values(PAGE_DATA).forEach(({ path, component, requireAuth, excludeLayout }) => {
    const componentElement = withSuspense(component);
    const hasUserIdParam = path.includes('/:userId');

    if (requireAuth) {
      routes.push({
        path,
        loader: async () => {
          const isAuthenticated = await checkAuthStatus();
          if (!isAuthenticated) {
            throw new Response("", {
              status: 401,
              statusText: "Unauthorized"
            });
          }
          if (hasUserIdParam) {
            return workspaceLoader();
          }
          return null;
        },
        element: excludeLayout ? componentElement : baseLayout(componentElement, hasUserIdParam),
        errorElement: <Navigate to="/login" replace />,
      });
    } else {
      routes.push({
        path,
        element: excludeLayout ? componentElement : baseLayout(componentElement, hasUserIdParam)
      });
    }
  });

  return routes;
};

/**
 * 앱플리케이션의 라우팅을 성합니다.
 * - 기본 레이아웃 적용
 * - 404 페이지 처리
 * - 동적 라우트 생성
 */
export const appRouter = createBrowserRouter(generateRoutes());